import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface AlibabaWanx26T2vParams {
  /**
   * 输入参数
   */
  input?: {
    /**
     * 视频生成提示词，最大长度2000字符
     * @length 0 - 2000
     */
    prompt: string;
    /**
     * 自定义背景音乐URL（可选）
     */
    audio_url?: string;
  };
  /**
   * 生成参数
   */
  parameters?: {
    /**
     * 视频分辨率，可选值：480P、720P、1080P
     * @default "1080P"
     * @values 480P | 720P | 1080P
     */
    resolution?: '480P' | '720P' | '1080P';
    /**
     * 视频时长（秒），可选值：5、10、15
     * @default 5
     * @values 5 | 10 | 15
     */
    duration?: 5 | 10 | 15;
    /**
     * 是否启用提示词扩展
     * @default true
     */
    prompt_extend?: boolean;
    /**
     * 是否添加水印
     * @default false
     */
    watermark?: boolean;
    /**
     * 是否生成音频（使用参考视频时必须为false）
     * @default true
     */
    audio?: boolean;
    /**
     * 是否启用多镜头智能生成模式（仅在prompt_extend=true时生效）
     */
    multi_shots?: boolean;
    /**
     * 随机种子，用于复现生成结果
     */
    seed?: number;
    /**
     * 视频分辨率，支持 '832*480', '480*832', '624*624', '1280*720', '720*1280', '960*960', '1088*832', '832*1088', '1920*1080', '1080*1920', '1440*1440', '1632*1248', '1248*1632'
     * @default "1920*1080"
     * @values 832*480 | 480*832 | 624*624 | 1280*720 | 720*1280 | 960*960 | 1088*832 | 832*1088 | 1920*1080 | 1080*1920 | 1440*1440 | 1632*1248 | 1248*1632
     */
    size?: '832*480' | '480*832' | '624*624' | '1280*720' | '720*1280' | '960*960' | '1088*832' | '832*1088' | '1920*1080' | '1080*1920' | '1440*1440' | '1632*1248' | '1248*1632';
  };
}

export interface AlibabaWanx26T2vResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * alibaba_wanx26_t2v
 *
 * <p>API 文档: https://help.aliyun.com/zh/model-studio/text-to-video-api-reference</p>
 * <p>Alibaba Wanx2.6 T2V 文生视频生成接口，支持最高1080p分辨率</p>
 * 
 *
 * @example
 * ```typescript
 * import { initSeacloud, alibabaWanx26T2v } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await alibabaWanx26T2v({
 *   // 参数见 AlibabaWanx26T2vParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 AlibabaWanx26T2vParams 接口定义
 * @returns 生成的资源列表
 */
export async function alibabaWanx26T2v(
  params: AlibabaWanx26T2vParams,
): Promise<AlibabaWanx26T2vResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 5000,
    maxAttempts: 120,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'alibaba_wanx26_t2v',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: AlibabaWanx26T2vResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
